import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Dashboard as DashboardIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Lock as LockIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useGetDashboardsQuery, useDeleteDashboardMutation, Dashboard } from '../../../redux/wallboardApi';
import { useAppSelector } from '../../../redux/hooks';
import { selectCurrentUser } from '../../../redux/reducers/authSlice';
import PageWithHeader from '../../common/PageWithHeader';

/**
 * Dashboard List Page
 * 
 * Displays all dashboards accessible to the current user.
 * Allows creating, editing, and deleting dashboards.
 */
export const DashboardListPage: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  
  const { data: dashboards, isLoading, error } = useGetDashboardsQuery();
  const [deleteDashboard, { isLoading: isDeleting }] = useDeleteDashboardMutation();

  const canCreateDashboard = currentUser?.roles?.some(role => 
    ['admin', 'dashboard-creator', 'ceo', 'c-suite', 'manager'].includes(role)
  );

  const canEditDashboard = (dashboard: Dashboard): boolean => {
    if (!currentUser) return false;
    return dashboard.createdBy === currentUser.id || currentUser.roles?.includes('admin') === true;
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this dashboard?')) {
      try {
        await deleteDashboard(id).unwrap();
      } catch (err) {
        console.error('Failed to delete dashboard:', err);
      }
    }
  };

  if (isLoading) {
    return (
      <PageWithHeader>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </PageWithHeader>
    );
  }

  if (error) {
    return (
      <PageWithHeader>
        <Alert severity="error">
          Failed to load dashboards. Please try again later.
        </Alert>
      </PageWithHeader>
    );
  }

  return (
    <PageWithHeader>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Dashboards
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage dynamic reporting dashboards
            </Typography>
          </Box>
          
          {canCreateDashboard && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/wallboard/new')}
            >
              Create Dashboard
            </Button>
          )}
        </Box>

        {/* Dashboard Grid */}
        {dashboards && dashboards.length > 0 ? (
          <Grid container spacing={3}>
            {dashboards.map((dashboard) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={dashboard.id}>
                <DashboardCard
                  dashboard={dashboard}
                  canEdit={canEditDashboard(dashboard)}
                  onView={() => navigate(`/wallboard/view/${dashboard.id}`)}
                  onEdit={() => navigate(`/wallboard/edit/${dashboard.id}`)}
                  onDelete={() => handleDelete(dashboard.id)}
                  isDeleting={isDeleting}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              textAlign: 'center',
            }}
          >
            <DashboardIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Dashboards Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {canCreateDashboard
                ? 'Create your first dashboard to get started.'
                : 'No dashboards are available for your role.'}
            </Typography>
            {canCreateDashboard && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/wallboard/new')}
              >
                Create Dashboard
              </Button>
            )}
          </Box>
        )}
      </Box>
    </PageWithHeader>
  );
};

/**
 * Dashboard Card Component
 */
interface DashboardCardProps {
  dashboard: Dashboard;
  canEdit: boolean;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  dashboard,
  canEdit,
  onView,
  onEdit,
  onDelete,
  isDeleting,
}) => {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <DashboardIcon sx={{ mr: 1, color: dashboard.metadata?.color || 'primary.main' }} />
          <Typography variant="h6" noWrap>
            {dashboard.name}
          </Typography>
        </Box>
        
        {dashboard.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              mb: 1,
            }}
          >
            {dashboard.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
          <Tooltip title={dashboard.isPublished ? 'Published' : 'Draft'}>
            <Chip
              icon={dashboard.isPublished ? <PublicIcon /> : <LockIcon />}
              label={dashboard.isPublished ? 'Published' : 'Draft'}
              size="small"
              color={dashboard.isPublished ? 'success' : 'default'}
              variant="outlined"
            />
          </Tooltip>
          
          <Chip
            label={`${dashboard.widgets.length} widgets`}
            size="small"
            variant="outlined"
          />
          
          {dashboard.metadata?.category && (
            <Chip
              label={dashboard.metadata.category}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
        <Tooltip title="View Dashboard">
          <IconButton onClick={onView} color="primary">
            <ViewIcon />
          </IconButton>
        </Tooltip>
        
        {canEdit && (
          <>
            <Tooltip title="Edit Dashboard">
              <IconButton onClick={onEdit}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Delete Dashboard">
              <IconButton onClick={onDelete} color="error" disabled={isDeleting}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </CardActions>
    </Card>
  );
};

export default DashboardListPage;
